import { MagnifyingGlass, Funnel, Buildings, CalendarBlank, X, Table, ChartBar, SealCheck } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useT } from '@/i18n/LanguageContext';
import { FORM_TYPES, STATUS_OPTIONS, monthLabel } from './historyUtils';

export function HistoryFilters({
  searchTerm, setSearchTerm,
  filterType, setFilterType,
  filterDept, setFilterDept,
  filterMonth, setFilterMonth,
  filterStatus, setFilterStatus,
  wfEnabled, departmentOptions, monthOptions,
  hasActiveFilters, resetFilters,
  filteredCount, totalCount,
  viewMode, setViewMode,
}) {
  const { t } = useT();
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-3 mb-3">
      <div className="flex flex-wrap items-center gap-2">
        {/* Search — compact, fixed width */}
        <div className="relative w-full sm:w-64">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder={t('hist.searchPh')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-input"
            className="pl-9 h-9 border-zinc-300 text-sm"
          />
        </div>

        {/* Divider */}
        <div className="hidden sm:block h-6 w-px bg-zinc-200 mx-1" />

        {/* Form type */}
        <div className="w-full sm:w-52">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger data-testid="filter-form-type" className="h-9 border-zinc-300 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <Funnel size={15} weight="duotone" className="text-zinc-500 shrink-0" />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              {FORM_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {t(type.key)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Department */}
        <div className="w-full sm:w-44">
          <Select value={filterDept} onValueChange={setFilterDept}>
            <SelectTrigger data-testid="filter-department" className="h-9 border-zinc-300 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <Buildings size={15} weight="duotone" className="text-zinc-500 shrink-0" />
                <SelectValue placeholder={t('hist.filter.deptPh')} />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('hist.filter.allDepts')}</SelectItem>
              {departmentOptions.map((d) => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Month */}
        <div className="w-full sm:w-56">
          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger data-testid="filter-month" className="h-9 border-zinc-300 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <CalendarBlank size={15} weight="duotone" className="text-zinc-500 shrink-0" />
                <SelectValue placeholder={t('hist.filter.monthPh')} />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('hist.filter.allMonths')}</SelectItem>
              {monthOptions.map((m) => {
                const [y, mo] = m.split('-');
                return <SelectItem key={m} value={m}>{monthLabel(t, parseInt(y, 10), parseInt(mo, 10))}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Verification status */}
        {wfEnabled && (
        <div className="w-full sm:w-44">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="filter-status" className="h-9 border-zinc-300 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <SealCheck size={15} weight="duotone" className="text-zinc-500 shrink-0" />
                <SelectValue placeholder={t('hist.filter.statusPh')} />
              </div>
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{t(o.key)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        )}

        {/* Reset filters + result count */}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={resetFilters}
            data-testid="reset-filters-btn"
            className="h-9 inline-flex items-center gap-1 px-2.5 text-xs text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded transition-colors"
            title={t('hist.clearTip')}
          >
            <X size={14} /> {t('hist.clear')}
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <div className="text-xs text-zinc-500 tabular-nums">
            {t('hist.count', { a: filteredCount, b: totalCount })}
          </div>
          {/* View toggle: 一覧 / ピボット */}
          <div className="inline-flex items-center bg-zinc-100 rounded-md p-0.5" data-testid="view-mode-toggle">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              data-testid="view-mode-list"
              className={`h-8 px-2.5 inline-flex items-center gap-1 text-xs font-medium rounded transition-colors ${
                viewMode === 'list' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              <Table size={14} weight="duotone" /> {t('hist.view.list')}
            </button>
            <button
              type="button"
              onClick={() => setViewMode('pivot')}
              data-testid="view-mode-pivot"
              className={`h-8 px-2.5 inline-flex items-center gap-1 text-xs font-medium rounded transition-colors ${
                viewMode === 'pivot' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              <ChartBar size={14} weight="duotone" /> {t('hist.view.pivot')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
